import { Migration } from '@gleamer/types';
import classNames from 'classnames';
import React, { useState } from 'react';
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  MIGRATION_POLL_MS,
  useMigrationCounts,
  useMigrationErrorGroups,
  useMigrationRun,
  useRefreshMigrationRun,
} from '../../../../hooks/useMigration';
import { PageButton } from '../../../atoms/PageButton';
import { PageTitle } from '../../../atoms/PageTitle';
import { TableBodyTd, TableHeadTh } from '../../../atoms/Table';
import { formatDateTime } from '../imports/format';
import { TraceLink } from '../TraceLink';
import { ProgressBar } from '../imports/ProgressBar';
import { MigrationCatchUpSection } from './MigrationCatchUpSection';
import {
  copyIsOver,
  countsSummary,
  KindsGrouping,
  MigrationKindsSection,
} from './MigrationKindsSection';
import { MigrationErrorsSection } from './MigrationErrorsSection';
import { MigrationSegmentsSection } from './MigrationSegmentsSection';
import {
  CommandButton,
  formatCount,
  PHASE_LABELS,
  RunStateBadge,
} from './parts';
import { DATE_LOCALE } from '../../../../utils/locale';

type MigrationRun = Migration.MigrationRun;
type MigrationGroupProgress = Migration.MigrationGroupProgress;
type MigrationStep = Migration.MigrationStep;
type AvailableCommands = ReturnType<typeof availableCommands>;

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <dt className="font-bold text-dl-text">{label}</dt>
      <dd className="break-all">{children}</dd>
    </>
  );
}

/**
 * Whether each command means anything in the run's state — migration-service ignores or refuses the rest
 * (MigrationRequestHandler). A replay is also refused while a worker holds what it would reset: the groups
 * table offers it only on a group with no segment running, the segments table only on a segment not running.
 */
export function availableCommands(run: MigrationRun) {
  const steering = ['RUNNING', 'PAUSING', 'PAUSED'].includes(run.state);
  return {
    pause: run.state === 'RUNNING',
    resume: run.state === 'PAUSING' || run.state === 'PAUSED',
    stop: steering || run.state === 'DONE_WITH_ERRORS',
    throttle: steering,
    retry: steering && run.segments.failed > 0,
    retrySegment: steering,
    replay:
      steering || run.state === 'DONE' || run.state === 'DONE_WITH_ERRORS',
    rerunErrors: steering || run.state === 'DONE_WITH_ERRORS',
    // the catch-up's own stop, on its view; a run's stop stops it too
    stopCatchUp: run.catchUp?.state === 'FOLLOWING',
  };
}

function Throttle({ run }: { run: MigrationRun }) {
  const [value, setValue] = useState(
    run.maxWorkers === null ? '' : String(run.maxWorkers)
  );
  const parsed = value.trim() === '' ? null : Number(value);
  const valid = parsed === null || (Number.isInteger(parsed) && parsed >= 1);
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <label className="text-sm">
        Segments at once{' '}
        <input
          type="number"
          min={1}
          placeholder="no limit"
          className="w-24 rounded-md border border-dl-strong px-2 py-1 text-black"
          value={value}
          onChange={e => setValue(e.target.value)}
        />
      </label>
      <CommandButton
        label="Throttle"
        disabled={!valid || parsed === run.maxWorkers}
        command={{ type: 'THROTTLE', runId: run.id, maxWorkers: parsed }}
      />
    </span>
  );
}

function Commands({ run, can }: { run: MigrationRun; can: AvailableCommands }) {
  return (
    <section
      aria-label="Commands"
      className="mb-4 flex flex-wrap items-center gap-2"
    >
      {can.pause && (
        <CommandButton
          label="Pause"
          command={{ type: 'PAUSE', runId: run.id }}
        />
      )}
      {can.resume && (
        <CommandButton
          label="Resume"
          command={{ type: 'RESUME', runId: run.id }}
        />
      )}
      {can.stop && (
        <CommandButton
          label="Stop"
          confirm={
            can.stopCatchUp
              ? 'Stop the run? It cannot be resumed; a new run starts over. Its catch-up stops too.'
              : 'Stop the run? It cannot be resumed; a new run starts over.'
          }
          command={{ type: 'STOP', runId: run.id }}
        />
      )}
      {can.retry && (
        <CommandButton
          label="Retry failed segments"
          command={{ type: 'RETRY', runId: run.id }}
        />
      )}
      {can.throttle && (
        // keyed on the server's value: a throttle set elsewhere replaces what the field shows
        <Throttle key={String(run.maxWorkers)} run={run} />
      )}
    </section>
  );
}

function Steps({ steps }: { steps: MigrationStep[] }) {
  if (steps.length === 0) {
    return null;
  }
  return (
    <section aria-labelledby="steps-title" className="mb-4">
      <h3 id="steps-title" className="mb-1 text-xl font-bold">
        Copies run by Google
      </h3>
      <table className="w-full table-auto">
        <thead className="bg-dl-track">
          <tr>
            <TableHeadTh>Type</TableHeadTh>
            <TableHeadTh>Source</TableHeadTh>
            <TableHeadTh>State</TableHeadTh>
            <TableHeadTh>Objects</TableHeadTh>
            <TableHeadTh>Bytes</TableHeadTh>
            <TableHeadTh>Polled</TableHeadTh>
          </tr>
        </thead>
        <tbody>
          {steps.map(step => (
            <tr key={step.id}>
              <TableBodyTd title={step.externalId ?? undefined}>
                {step.type}
              </TableBodyTd>
              <TableBodyTd className="break-all font-mono text-sm">
                {step.source}
              </TableBodyTd>
              <TableBodyTd title={step.error ?? undefined}>
                {step.state}
                {step.error && (
                  <div className="text-sm text-dl-danger">{step.error}</div>
                )}
              </TableBodyTd>
              <TableBodyTd>{formatCount(step.objects)}</TableBodyTd>
              <TableBodyTd>{formatCount(step.bytes)}</TableBodyTd>
              <TableBodyTd>{formatDateTime(step.polledAt)}</TableBodyTd>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** What the page shows: the tiles, or one of them opened in their place. Kept in the URL, for the back button. */
type View =
  | 'overview'
  | 'run'
  | 'kinds'
  | 'copies'
  | 'segments'
  | 'errors'
  | 'catch-up';

const VIEW_TITLES: Record<Exclude<View, 'overview'>, string> = {
  run: 'Run',
  kinds: 'Kinds',
  copies: 'Copies run by Google',
  segments: 'Segments',
  errors: 'Errors',
  'catch-up': 'Catch-up',
};

function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit++;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: unit === 0 ? 0 : 1 })} ${units[unit]}`;
}

function formatDuration(from: string | null, to: string | null): string {
  if (!from) {
    return '—';
  }
  const minutes = Math.max(
    0,
    Math.round(((to ? Date.parse(to) : Date.now()) - Date.parse(from)) / 60000)
  );
  return minutes < 60
    ? `${minutes} min`
    : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

/** A summary that opens its details: the whole tile is the button. */
function Tile({
  title,
  metric,
  tone = 'neutral',
  onOpen,
  children,
}: React.PropsWithChildren<{
  title: string;
  metric: React.ReactNode;
  tone?: 'neutral' | 'good' | 'warning' | 'bad';
  onOpen: () => void;
}>) {
  return (
    <button
      type="button"
      aria-label={`Open ${title}`}
      onClick={onOpen}
      className={classNames(
        'group flex min-h-[9rem] flex-col rounded-2xl border bg-dl-surface p-4 text-left shadow-sm transition',
        'hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-dl-primary',
        {
          'border-dl-border': tone === 'neutral' || tone === 'good',
          'border-dl-warning/40': tone === 'warning',
          'border-dl-danger/40': tone === 'bad',
        }
      )}
    >
      <span className="flex w-full items-center justify-between text-sm font-medium uppercase tracking-wide text-dl-muted">
        {title}
        <span
          aria-hidden
          className="text-dl-faint transition group-hover:translate-x-0.5 group-hover:text-dl-accent"
        >
          →
        </span>
      </span>
      <span
        className={classNames('mt-2 text-3xl font-semibold', {
          'text-dl-text': tone === 'neutral',
          'text-dl-success': tone === 'good',
          'text-dl-warning': tone === 'warning',
          'text-dl-danger': tone === 'bad',
        })}
      >
        {metric}
      </span>
      <span className="mt-auto w-full pt-2 text-sm text-dl-muted">
        {children}
      </span>
    </button>
  );
}

function segmentBar(label: string, s: Migration.MigrationSegmentCounts) {
  return (
    <ProgressBar
      label={label}
      segments={[
        { label: 'Done', value: s.done, className: 'bg-dl-success' },
        { label: 'Failed', value: s.failed, className: 'bg-dl-danger' },
        { label: 'Running', value: s.running, className: 'bg-dl-primary' },
        { label: 'Pending', value: s.pending, className: 'bg-dl-faint' },
      ]}
    />
  );
}

/** The run at a glance: one tile per part of the page, each opening that part in place of the tiles. */
function Overview({
  run,
  groups,
  steps,
  open,
}: {
  run: MigrationRun;
  groups: MigrationGroupProgress[];
  steps: MigrationStep[];
  open: (view: View) => void;
}) {
  const errorGroups = useMigrationErrorGroups(String(run.id)).data ?? [];
  const counts = countsSummary(useMigrationCounts(String(run.id)).data ?? []);
  // while the run copies, its progress; once it is over and counted, whether every kind arrived
  const showCounts = copyIsOver(run.state) && counts.migrated > 0;
  const topErrors = errorGroups.filter(g => g.unacknowledged > 0).slice(0, 3);
  const s = run.segments;
  const groupsDone = groups.filter(
    g => g.segments.total > 0 && g.segments.done === g.segments.total
  );
  const copies = {
    done: steps.filter(step => step.state === 'DONE').length,
    failed: steps.filter(step => step.state === 'FAILED').length,
    objects: steps.reduce((sum, step) => sum + step.objects, 0),
    bytes: steps.reduce((sum, step) => sum + step.bytes, 0),
  };
  return (
    <section
      aria-label="Overview"
      className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
    >
      <Tile
        title={VIEW_TITLES.run}
        metric={formatDuration(run.startedAt, run.finishedAt)}
        onOpen={() => open('run')}
      >
        Bulk copy
        {run.requestedBy && ` · requested by ${run.requestedBy}`}
        <br />
        {formatCount(run.counters.written)} written ·{' '}
        {formatCount(run.counters.read)} read ·{' '}
        {formatCount(run.counters.skipped)} skipped
      </Tile>
      <Tile
        title={VIEW_TITLES.kinds}
        metric={
          showCounts
            ? `${counts.agree} / ${counts.migrated}`
            : `${groupsDone.length} / ${groups.length}`
        }
        tone={
          showCounts
            ? counts.differ > 0
              ? 'warning'
              : counts.pending > 0
                ? 'neutral'
                : 'good'
            : groups.length > 0 && groupsDone.length === groups.length
              ? 'good'
              : 'neutral'
        }
        onOpen={() => open('kinds')}
      >
        {showCounts ? (
          <>
            <span className="block">
              kinds whose counts agree
              {counts.differ > 0 && (
                <span className="text-dl-warning">
                  {' '}
                  · {counts.differ} differ
                </span>
              )}
              {counts.pending > 0 && ` · ${counts.pending} still counting`}
            </span>
            <span className="text-sm text-dl-faint">
              {groupsDone.length} / {groups.length} groups copied
            </span>
          </>
        ) : (
          <>
            <span className="mb-1 block">groups copied</span>
            {segmentBar('Segments of every group', s)}
          </>
        )}
      </Tile>
      <Tile
        title={VIEW_TITLES.segments}
        metric={`${formatCount(s.done)} / ${formatCount(s.total)}`}
        tone={s.failed > 0 ? 'bad' : 'neutral'}
        onOpen={() => open('segments')}
      >
        {s.failed > 0 ? (
          <span className="text-dl-danger">{formatCount(s.failed)} failed</span>
        ) : (
          'none failed'
        )}
        {s.running > 0 && ` · ${formatCount(s.running)} running`}
        {s.pending > 0 && ` · ${formatCount(s.pending)} pending`}
      </Tile>
      {steps.length > 0 && (
        <Tile
          title={VIEW_TITLES.copies}
          metric={`${copies.done} / ${steps.length}`}
          tone={
            copies.failed > 0
              ? 'bad'
              : copies.done === steps.length
                ? 'good'
                : 'neutral'
          }
          onOpen={() => open('copies')}
        >
          {copies.failed > 0 && (
            <span className="text-dl-danger">{copies.failed} failed · </span>
          )}
          {formatCount(copies.objects)} objects · {formatBytes(copies.bytes)}
        </Tile>
      )}
      {run.catchUp && (
        <Tile
          title={VIEW_TITLES['catch-up']}
          metric={formatCount(run.catchUp.pending)}
          tone={
            run.catchUp.failed > 0 || run.catchUp.conflicts > 0
              ? 'bad'
              : 'neutral'
          }
          onOpen={() => open('catch-up')}
        >
          {PHASE_LABELS[run.catchUp.phase]} · changes pending
          {run.catchUp.failed > 0 && (
            <span className="text-dl-danger">
              {' '}
              · {formatCount(run.catchUp.failed)} failed
            </span>
          )}
          {run.catchUp.conflicts > 0 && (
            <span className="text-dl-danger">
              {' '}
              · {formatCount(run.catchUp.conflicts)} conflicts
            </span>
          )}
        </Tile>
      )}
      <Tile
        title={VIEW_TITLES.errors}
        metric={formatCount(run.unacknowledgedErrors)}
        tone={run.unacknowledgedErrors > 0 ? 'warning' : 'good'}
        onOpen={() => open('errors')}
      >
        {topErrors.length === 0 ? (
          'unacknowledged: none'
        ) : (
          <span className="flex flex-col gap-0.5">
            {topErrors.map(g => (
              <span
                key={`${g.kind}/${g.reason}`}
                className="flex justify-between gap-2"
              >
                <span className="truncate">
                  {g.kind} · <span>{g.reason}</span>
                </span>
                <span>{formatCount(g.unacknowledged)}</span>
              </span>
            ))}
          </span>
        )}
      </Tile>
    </section>
  );
}

function RunFields({ run }: { run: MigrationRun }) {
  return (
    <dl
      className="grid gap-x-4 gap-y-1"
      style={{ gridTemplateColumns: 'max-content 1fr' }}
    >
      <Field label="Type">Bulk copy</Field>
      <Field label="Source">{run.sourceLabel ?? run.source}</Field>
      <Field label="Catch-up">
        {run.catchUp
          ? PHASE_LABELS[run.catchUp.phase]
          : 'none: the source’s changes are not followed'}
      </Field>
      <Field label="Groups">{run.groups.join(', ')}</Field>
      {run.bigqueryExcluded.length > 0 && (
        <Field label="BigQuery left out">
          {run.bigqueryExcluded.join(', ')}
        </Field>
      )}
      <Field label="Segments at once">{run.maxWorkers ?? 'no limit'}</Field>
      <Field label="Requested by">{run.requestedBy ?? '—'}</Field>
      <Field label="Requested">{formatDateTime(run.requestedAt)}</Field>
      <Field label="Started">{formatDateTime(run.startedAt)}</Field>
      <Field label="Finished">{formatDateTime(run.finishedAt)}</Field>
      <Field label="Duration">
        {formatDuration(run.startedAt, run.finishedAt)}
      </Field>
      <Field label="Entities">
        {formatCount(run.counters.read)} read,{' '}
        {formatCount(run.counters.written)} written,{' '}
        {formatCount(run.counters.skipped)} skipped
      </Field>
    </dl>
  );
}

export function MigrationRunPage() {
  const { runId } = useParams();
  const location = useLocation();
  const listSearch = (location.state as { listSearch?: string } | null)
    ?.listSearch;
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, error, isError, isFetching, dataUpdatedAt } =
    useMigrationRun(runId);
  const refresh = useRefreshMigrationRun(runId);
  const kindCounts = useMigrationCounts(runId).data ?? [];

  const backLink = (
    <Link
      className="text-dl-accent underline"
      to={{
        pathname: '/backoffice/migration',
        search: listSearch ? `?${listSearch}` : '',
      }}
    >
      ← All runs
    </Link>
  );

  // a reload that fails keeps what the page showed (react-query keeps the data): only a first read replaces it
  if (!data && isError) {
    return (
      <div>
        {backLink}
        <div role="alert" className="mt-2 text-dl-danger">
          Could not load migration run {runId}: {(error as Error)?.message}
        </div>
      </div>
    );
  }

  if (!data) {
    return <div>Loading…</div>;
  }

  const { run, groups, steps } = data;
  const can = availableCommands(run);
  // the Groups and Counts views of before 2026-10-26 are the kinds, by group and by status
  const asked = searchParams.get('view') ?? 'overview';
  const requested = (
    asked === 'groups' || asked === 'counts' ? 'kinds' : asked
  ) as View;
  const grouping: KindsGrouping =
    searchParams.get('by') === 'group' || asked === 'groups'
      ? 'group'
      : searchParams.get('by') === 'status' || asked === 'counts'
        ? 'status'
        : copyIsOver(run.state)
          ? 'status'
          : 'group';
  // a view the run does not have (a catch-up of a source not followed, an old link) falls back to the tiles
  const available: Record<View, boolean> = {
    overview: true,
    run: true,
    kinds: true,
    segments: true,
    copies: steps.length > 0,
    'catch-up': !!run.catchUp,
    errors: true,
  };
  const view: View = available[requested] ? requested : 'overview';
  const open = (next: View) =>
    setSearchParams(next === 'overview' ? {} : { view: next }, {
      state: location.state,
    });
  const groupKinds = (by: KindsGrouping) =>
    setSearchParams(
      { view: 'kinds', by },
      { state: location.state, replace: true }
    );

  return (
    <div>
      {backLink}
      <PageTitle
        className="mt-2 items-center"
        title={`Migration run #${run.id}`}
      >
        <PageButton onClick={refresh} disabled={isFetching}>
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </PageButton>
      </PageTitle>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <RunStateBadge state={run.state} />
        <TraceLink traceId={run.traceId} logsUrl={run.logsUrl} />
        {dataUpdatedAt > 0 && (
          <span className="text-sm text-dl-muted">
            read at {new Date(dataUpdatedAt).toLocaleTimeString(DATE_LOCALE)}, checked
            every {MIGRATION_POLL_MS / 1000} s while it moves
          </span>
        )}
      </div>

      {isError && (
        <div role="alert" className="mb-2 text-sm text-dl-danger">
          Could not read the run again: {(error as Error)?.message}. It shows
          its last read.
        </div>
      )}

      {run.state === 'DONE_WITH_ERRORS' && (
        <div
          role="status"
          className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-dl-warning/40 bg-dl-warning-tint p-3 text-dl-warning"
        >
          <span className="mr-auto">
            Everything is copied but {formatCount(run.unacknowledgedErrors)}{' '}
            error(s): acknowledge them to close the run, or fix them at the
            source and rerun them.
          </span>
          {view !== 'errors' && (
            <PageButton
              className="border-dl-warning/40 bg-dl-surface"
              onClick={() => open('errors')}
            >
              Go to the errors
            </PageButton>
          )}
        </div>
      )}

      <Commands run={run} can={can} />

      {view === 'overview' ? (
        <Overview run={run} groups={groups} steps={steps} open={open} />
      ) : (
        <div className="rounded-2xl border border-dl-border bg-dl-surface p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-3">
            <PageButton className="mx-0" onClick={() => open('overview')}>
              ← Overview
            </PageButton>
            {view === 'run' && (
              <h3 className="text-xl font-bold">{VIEW_TITLES.run}</h3>
            )}
          </div>
          {view === 'run' && <RunFields run={run} />}
          {view === 'catch-up' && <MigrationCatchUpSection run={run} />}
          {view === 'kinds' && (
            <MigrationKindsSection
              run={run}
              groups={groups}
              counts={kindCounts}
              grouping={grouping}
              onGrouping={groupKinds}
              canReplay={can.replay}
            />
          )}
          {view === 'copies' && <Steps steps={steps} />}
          {view === 'segments' && (
            <MigrationSegmentsSection
              runId={run.id}
              groups={groups.map(g => g.group)}
              canReplay={can.replay}
              canRetry={can.retrySegment}
            />
          )}
          {view === 'errors' && (
            <MigrationErrorsSection run={run} canRerun={can.rerunErrors} />
          )}
        </div>
      )}
    </div>
  );
}

export default MigrationRunPage;
